import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCompanyId } from '../../services/CompanyidProvider';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  Fade,
  Snackbar,
  Alert,
  Autocomplete
} from '@mui/material';
import {
  History as HistoryIcon,
  List as ListIcon,
  ContentCopy as CopyIcon,
  Search as SearchIcon,
  WarningAmberRounded as WarnIcon,
  Business as BusinessIcon,
  Refresh as RefreshIcon,
  RestartAlt as ResetIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  ArrowBack as BackIcon
} from '@mui/icons-material';

// ─── Import API Service ────────────────────────────────────
import {
  getEntitlements,
  getEntitlementDefinitions,
  getEntitlementList,
  updateSubscription
} from '../../services/entitlements.service';

// GSD Theme Colors
const T = {
  teal: '#188C8E',
  tealDark: '#0F6E70',
  tealLight: '#D6F3F3',
  tealExtraLight: '#E8F8F8',
  amber: '#F59E0B',
  danger: '#DC2626',
  success: '#10B981',
  border: '#E2E8F0',
  rowChanged: '#FFFBEB',
  rowChangedBorder: '#F59E0B',
  textPrimary: '#000000',
};

// Skeleton rows
const SkeletonRows = ({ count = 6 }) =>
  Array.from({ length: count }).map((_, i) => (
    <TableRow key={i}>
      <TableCell><Skeleton variant="text" width="60%" animation="wave" sx={{ bgcolor: T.tealExtraLight }} /></TableCell>
      <TableCell><Skeleton variant="rectangular" width={100} height={40} sx={{ borderRadius: 1, bgcolor: T.tealExtraLight }} animation="wave" /></TableCell>
      <TableCell><Skeleton variant="text" width="80%" animation="wave" sx={{ bgcolor: T.tealExtraLight }} /></TableCell>
    </TableRow>
  ));

// ─── Value input dialog for number entitlements ──────────
const ValueInputDialog = ({ open, onClose, entitlement, onConfirm }) => {
  const [value, setValue] = useState('');

  useEffect(() => {
    if (entitlement && open) {
      setValue('');
    }
  }, [entitlement, open]);

  if (!entitlement) return null;

  const handleNumberChange = (e) => {
    const newValue = e.target.value;
    if (newValue === '' || /^\d+$/.test(newValue)) {
      setValue(newValue);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: 18, fontWeight: 700, color: T.teal }}>
        Enter Value for {entitlement.name}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2, fontSize: 14, color: '#000000' }}>
          {entitlement.description}
        </Typography>
        <TextField
          autoFocus
          fullWidth
          type="text"
          label="Value"
          value={value}
          onChange={handleNumberChange}
          placeholder="Enter a number"
          helperText="Enter a positive number"
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: '#000000' }}>Cancel</Button>
        <Button 
          onClick={() => {
            const numValue = parseInt(value, 10);
            if (!isNaN(numValue) && numValue > 0) {
              onConfirm(entitlement, numValue);
            }
          }} 
          variant="contained" 
          sx={{ bgcolor: T.teal }}
          disabled={!value || parseInt(value, 10) <= 0}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Main component
const EntitlementsTab = ({ companyId: propCompanyId, companyName: propCompanyName, onNavigateHistory }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { companyId: contextCompanyId } = useCompanyId();
  
  // ─── Get company ID and name from navigation state ────────
  const navigationState = location.state || {};
  const initialCompanyId = navigationState.companyId || propCompanyId || contextCompanyId;
  const initialCompanyName = navigationState.companyName || propCompanyName || '';
  
  const companyId = initialCompanyId;
  const [displayCompanyName, setDisplayCompanyName] = useState(initialCompanyName || '');

  // Data state
  const [existingEntitlements, setExistingEntitlements] = useState([]);
  const [originalEntitlements, setOriginalEntitlements] = useState([]);
  const [editedValues, setEditedValues] = useState({});
  const [selectedToAdd, setSelectedToAdd] = useState([]);
  const [allAvailable, setAllAvailable] = useState([]);
  const [companyNameState] = useState(initialCompanyName || '');

  // ─── Cache for definitions ──────────────────────────────────
  const [definitionsCache, setDefinitionsCache] = useState({});
  const [definitionsLoaded, setDefinitionsLoaded] = useState(false);
  const [definitionsLoading, setDefinitionsLoading] = useState(false);

  // Entitlement List state
  const [entitlementListData, setEntitlementListData] = useState([]);
  const [listLoading, setListLoading] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [filterText, setFilterText] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [listOpen, setListOpen] = useState(false);
  const [listSearchText, setListSearchText] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [copyDone, setCopyDone] = useState(false);
  const [unsavedNavTarget, setUnsavedNavTarget] = useState(null);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [valueDialogOpen, setValueDialogOpen] = useState(false);
  const [pendingEntitlement, setPendingEntitlement] = useState(null);
  const [selectedEntitlement, setSelectedEntitlement] = useState(null);
  const [searchInput, setSearchInput] = useState('');

  const hasUnsaved = selectedToAdd.length > 0 || Object.keys(editedValues).length > 0;
  const changeSummary = { adding: selectedToAdd.length, editing: Object.keys(editedValues).length };

  // ─── Filtered list for entitlement list dialog ────────────
  // Search by NAME only (not description)
  const filteredList = entitlementListData.filter(ent =>
    ent.name.toLowerCase().includes(listSearchText.toLowerCase())
  );

  // ─── Get available entitlements ────────────────────────────
  const getAvailableEntitlements = () => {
    const existingNames = existingEntitlements.map(e => e.name);
    const selectedNames = selectedToAdd.map(e => e.name);
    return allAvailable
      .filter(ent => !existingNames.includes(ent.name) && !selectedNames.includes(ent.name))
      .map(ent => ({
        ...ent,
        type: ent.type || 'feature'
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  // ─── Load Entitlement List ──────────────────────────────
  const loadEntitlementList = useCallback(async () => {
    setListLoading(true);
    try {
      const response = await getEntitlementList();
      if (response.status === 'ok') {
        setEntitlementListData(response.data || []);
      }
    } catch (error) {
      console.error('Failed to load entitlement list:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load entitlement list. Please try again.',
        severity: 'error'
      });
    } finally {
      setListLoading(false);
    }
  }, []);

  // ─── Load definitions once on mount ──────────────────────
  useEffect(() => {
    const loadDefinitions = async () => {
      if (definitionsLoaded || definitionsLoading) return;
      setDefinitionsLoading(true);
      try {
        const response = await getEntitlementDefinitions();
        if (response && response.status === 'ok') {
          const map = {};
          const typeMap = {};
          response.data.forEach(def => {
            map[def.name] = def.description;
            typeMap[def.name] = def.type || 'feature';
          });
          setDefinitionsCache(map);
          // Store types for later use
          window.__entitlementTypes = typeMap;
          setDefinitionsLoaded(true);
        }
      } catch (error) {
        console.error('Failed to load definitions:', error);
      } finally {
        setDefinitionsLoading(false);
      }
    };
    loadDefinitions();
  }, [definitionsLoaded, definitionsLoading]);

  // ─── Load Entitlements from API ────────────────────────────
  const loadEntitlements = useCallback(async () => {
    if (!companyId || companyId === 'Invalid Company - Enter a valid Company') return;
    setLoading(true);
    setEditedValues({});
    setSelectedToAdd([]);
    setSelectedEntitlement(null);
    
    try {
      // 1. Get existing entitlements (for the table)
      const entitlementsRes = await getEntitlements(companyId);
      
      console.log('📦 Full Response:', entitlementsRes);
      
      if (entitlementsRes && entitlementsRes.status === 'ok') {
        let data = {};
        
        if (entitlementsRes.subscription?.entitlements) {
          data = entitlementsRes.subscription.entitlements;
        } else if (entitlementsRes.data) {
          data = entitlementsRes.data;
        } else {
          data = entitlementsRes;
        }
        
        console.log('📋 Extracted Data:', data);
        
        // ✅ Preserve types from API
        const typeMap = window.__entitlementTypes || {};
        
        const arr = Object.keys(data).map(name => {
          const item = data[name];
          
          if (item && typeof item === 'object' && 'value' in item) {
            // ✅ Keep the original type from API
            let type = item.type || typeMap[name] || 'quantity';
            
            return {
              name: name,
              value: item.value,
              type: type,
              description: definitionsCache[name] || item.description || name
            };
          } else {
            // Fallback
            return {
              name: name,
              value: item,
              type: typeof item === 'boolean' ? 'feature' : 'quantity',
              description: definitionsCache[name] || name
            };
          }
        });
        
        console.log('📊 Parsed Entitlements:', arr);
        console.log('📊 Count:', arr.length);
        
        if (arr.length > 0) {
          setExistingEntitlements(arr);
          setOriginalEntitlements(JSON.parse(JSON.stringify(arr)));
        } else {
          setExistingEntitlements([]);
          setOriginalEntitlements([]);
          setSnackbar({
            open: true,
            message: 'No entitlements found for this company',
            severity: 'info'
          });
        }
        
        // 2. Get all entitlement definitions (for dropdown) with correct types
        const allDefinitions = Object.keys(definitionsCache).map(name => ({
          name: name,
          type: typeMap[name] || 'feature',
          description: definitionsCache[name] || name
        }));
        
        const existingNames = Object.keys(data);
        
        const availableEntitlements = allDefinitions
          .filter(def => !existingNames.includes(def.name))
          .map(def => ({
            name: def.name,
            type: def.type || 'feature',
            description: def.description || def.name
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        
        setAllAvailable(availableEntitlements);
      }
      
      setDisplayCompanyName(companyNameState || companyId);
    } catch (error) {
      console.error('Failed to load entitlements:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load entitlements. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [companyId, definitionsCache, companyNameState]);

  // ─── Load on mount ────────────────────────────────────────
  useEffect(() => {
    if (companyId && companyId !== 'Invalid Company - Enter a valid Company') {
      loadEntitlements();
    }
  }, [companyId, loadEntitlements]);

  // ─── Navigate back to Company Management ──────────────────
  const handleBack = () => {
    navigate('/company-management', {
      state: {
        companyId: companyId,
        companyName: companyNameState || displayCompanyName,
        opportunityId: navigationState.opportunityId || null,
        country: navigationState.country || null,
        buddyAccount: navigationState.buddyAccount || null,
        customerId: navigationState.customerId || null,
        crmInstanceId: navigationState.crmInstanceId || null,
        entitlementCount: existingEntitlements.length,  // ← use actual current count
        overrideCount: navigationState.overrideCount || 0,
        preserveState: true
      }
    });
};

  // Reset all changes
  const handleReset = () => {
    setResetting(true);
    setExistingEntitlements(JSON.parse(JSON.stringify(originalEntitlements)));
    setEditedValues({});
    setSelectedToAdd([]);
    setSelectedEntitlement(null);
    setFilterText('');
    setPage(1);
    setTimeout(() => {
      setResetting(false);
      setSnackbar({
        open: true,
        message: 'All changes have been reset successfully',
        severity: 'info'
      });
    }, 500);
  };

  const handleResetClick = () => {
    if (!hasUnsaved) {
      setSnackbar({
        open: true,
        message: 'No changes to reset',
        severity: 'info'
      });
      return;
    }
    setResetConfirmOpen(true);
  };

  // Refresh data
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadEntitlements();
    setFilterText('');
    setPage(1);
    setTimeout(() => {
      setRefreshing(false);
      setSnackbar({
        open: true,
        message: 'Data refreshed successfully',
        severity: 'success'
      });
    }, 500);
  };

  // ─── Handle Entitlement Selection ──────────────────────────
  const handleEntitlementSelect = async (entitlement) => {
    if (!entitlement) return;

    // ─── For quantity/number types, open dialog ────────────
    if (entitlement.type === 'quantity' || entitlement.type === 'number') {
      setPendingEntitlement(entitlement);
      setValueDialogOpen(true);
      setSelectedEntitlement(null);
      setSearchInput('');
      return;
    }

    // ─── For feature/boolean types, add directly with true ──
    if (entitlement.type === 'feature' || entitlement.type === 'boolean') {
      setSelectedToAdd(prev => [...prev, { 
        ...entitlement, 
        value: true,
        type: entitlement.type || 'feature'
      }]);
      
      setSnackbar({
        open: true,
        message: `"${entitlement.name}" added to pending list (value: true)`,
        severity: 'success'
      });
      
      setSelectedEntitlement(null);
      setSearchInput('');
      return;
    }

    // ─── Fallback ─────────────────────────────────────────────
    setSelectedToAdd(prev => [...prev, { 
      ...entitlement, 
      value: true,
      type: entitlement.type || 'feature'
    }]);
    
    setSnackbar({
      open: true,
      message: `"${entitlement.name}" added to pending list (value: true)`,
      severity: 'success'
    });
    
    setSelectedEntitlement(null);
    setSearchInput('');
  };

  // ─── Handle Value Dialog Confirm ──────────────────────────
  const handleValueDialogConfirm = (entitlement, value) => {
    setSelectedToAdd(prev => [...prev, { 
      ...entitlement, 
      value: value,
      type: entitlement.type || 'quantity'
    }]);
    setValueDialogOpen(false);
    setPendingEntitlement(null);
    setSelectedEntitlement(null);
    setSearchInput('');
    setSnackbar({
      open: true,
      message: `"${entitlement.name}" added to pending list with value: ${value}`,
      severity: 'success'
    });
  };

  // ─── Remove Chip ──────────────────────────────────────────
  const handleRemoveChip = (ent) => {
    setSelectedToAdd(prev => prev.filter(e => e.name !== ent.name));
  };

  // ─── Handle Value Change (with empty = 0 support) ──────
  const handleValueChange = (name, newVal) => {
    const original = existingEntitlements.find(e => e.name === name);
    
    const compareVal = newVal === '' ? 0 : newVal;
    const originalVal = original ? original.value : 0;
    
    if (String(originalVal) === String(compareVal)) {
      setEditedValues(prev => { const next = { ...prev }; delete next[name]; return next; });
    } else {
      setEditedValues(prev => ({ ...prev, [name]: newVal }));
    }
  };

  // ─── Handle Update ──────────────────────────────────────────
  const handleUpdate = async () => {
    setConfirmOpen(false);
    setUpdating(true);
    
    try {
      const subscription = {
        entitlements: {},
        basedOn: []
      };
      
      const currentEntitlements = existingEntitlements.map(ent => {
        const editedVal = editedValues[ent.name];
        const value = editedVal !== undefined ? (editedVal === '' ? 0 : editedVal) : ent.value;
        return {
          name: ent.name,
          value: value
        };
      });
      
      const allEntitlements = [...currentEntitlements, ...selectedToAdd];
      
      allEntitlements.forEach(ent => {
        subscription.entitlements[ent.name] = ent.value;
      });
      
      const response = await updateSubscription(companyId, subscription);
      
      if (response.status === 'ok') {
        const responseData = response.data.subscription || response.data;
        if (responseData && responseData.entitlements) {
          const arr = Object.keys(responseData.entitlements).map(name => ({
            name: name,
            value: responseData.entitlements[name],
            type: typeof responseData.entitlements[name] === 'boolean' ? 'feature' : 'quantity',
            description: name
          }));
          setExistingEntitlements(arr);
          setOriginalEntitlements(JSON.parse(JSON.stringify(arr)));
        }
        
        setSelectedToAdd([]);
        setEditedValues({});
        setSelectedEntitlement(null);
        
        setSnackbar({
          open: true,
          message: `Successfully updated ${changeSummary.adding + changeSummary.editing} entitlement(s)`,
          severity: 'success'
        });
        
        await loadEntitlements();
      } else {
        throw new Error(response.message || 'Update failed');
      }
      
    } catch (err) {
      console.error('Update failed:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Update failed. Please try again.',
        severity: 'error'
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdateClick = () => {
    if (!hasUnsaved) {
      setSnackbar({
        open: true,
        message: 'No changes to update',
        severity: 'info'
      });
      return;
    }
    setConfirmOpen(true);
  };

  const handleCopyId = () => {
    navigator.clipboard.writeText(companyId).then(() => {
      setCopyDone(true);
      setTimeout(() => setCopyDone(false), 2000);
    });
  };

const safeNavigate = async (target) => {
  if (hasUnsaved) {
    setUnsavedNavTarget(target);
  } else {
    if (target === 'history') {
      navigate('/companies/history', {
        state: {
          companyId,
          companyName: displayCompanyName,
          opportunityId: navigationState.opportunityId || null,
          country: navigationState.country || null,
          buddyAccount: navigationState.buddyAccount || null,
          customerId: navigationState.customerId || null,
          crmInstanceId: navigationState.crmInstanceId || null,
          entitlementCount: existingEntitlements.length,
          overrideCount: navigationState.overrideCount ?? 0,
          historyType: 'ENTITLEMENT'
        }
      });
      return;
    }

    if (target === 'list') {
      setListOpen(true);
      await loadEntitlementList();
    }
  }
};

  const confirmNavigation = () => {
  const target = unsavedNavTarget;

  setUnsavedNavTarget(null);
  setSelectedToAdd([]);
  setEditedValues({});

  if (target === 'history') {
    navigate('/companies/history', {
      state: {
        companyId,
        companyName: displayCompanyName,
        opportunityId: navigationState.opportunityId || null,
        country: navigationState.country || null,
        buddyAccount: navigationState.buddyAccount || null,
        customerId: navigationState.customerId || null,
        crmInstanceId: navigationState.crmInstanceId || null,
        entitlementCount: existingEntitlements.length,
        overrideCount: navigationState.overrideCount ?? 0,
        historyType: 'ENTITLEMENT'
      }
    });
    return;
  }

  if (target === 'list') {
    setListOpen(true);
    loadEntitlementList();
  }
};

  // Filter and paginate
  const filtered = existingEntitlements.filter(e =>
    e.name.toLowerCase().includes(filterText.toLowerCase())
  );
  
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const startIndex = filtered.length === 0 ? 0 : (page - 1) * pageSize + 1;
  const endIndex = Math.min(page * pageSize, filtered.length);
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);
  useEffect(() => { setPage(1); }, [filterText, pageSize]);

  // Guard: no company
  if (!companyId || companyId === 'Invalid Company - Enter a valid Company') {
    return (
      <Fade in>
        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3, bgcolor: T.tealExtraLight }} elevation={0}>
          <BusinessIcon sx={{ fontSize: 56, color: T.teal, mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#000000' }}>Please load a company to view entitlements.</Typography>
        </Paper>
      </Fade>
    );
  }

  // Search bar styling
  const searchBarSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: '#FAFAFA',
      fontSize: 14,
      '&:hover': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: T.teal,
        }
      },
      '&.Mui-focused': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: T.teal,
          borderWidth: 2,
        }
      }
    }
  };

  // Back button styling
  const backBtnStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#0D8F85",
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: ".3px",
    textTransform: "uppercase",
    padding: "6px 0",
    marginBottom: 8,
  };

  const availableEntitlements = getAvailableEntitlements();

  return (
    <Fade in timeout={300}>
      <Box sx={{ p: 4, bgcolor: T.tealExtraLight, minHeight: '100vh' }}>

        {/* Value Input Dialog */}
        <ValueInputDialog
          open={valueDialogOpen}
          onClose={() => setValueDialogOpen(false)}
          entitlement={pendingEntitlement}
          onConfirm={handleValueDialogConfirm}
        />

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            severity={snackbar.severity} 
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            sx={{ borderRadius: 2, fontWeight: 500, fontSize: 13 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Back Button */}
        <button onClick={handleBack} style={backBtnStyle}>
          <BackIcon sx={{ fontSize: 16 }} />
          Back
        </button>

        {/* Company Header (without search bar) */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderRadius: 2,
            bgcolor: 'white',
            border: `1px solid ${T.teal}`,
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: T.teal, mb: 1, letterSpacing: 0.5 }}>
              CURRENT COMPANY
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 20, color: '#000000' }}>
                {displayCompanyName || companyNameState || companyId}
              </Typography>
              <Typography sx={{ fontSize: 14, color: '#000000', fontFamily: 'monospace' }}>
                ID: {companyId}
              </Typography>
              <Tooltip title={copyDone ? 'Copied!' : 'Copy ID'}>
                <IconButton size="small" onClick={handleCopyId} sx={{ color: T.teal }}>
                  <CopyIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              size="medium" 
              startIcon={<HistoryIcon />} 
              onClick={() => safeNavigate('history')} 
              sx={{ color: T.teal, textTransform: 'none', fontWeight: 500, fontSize: 14 }}
            >
              Entitlement History
            </Button>
            <Button 
              size="medium" 
              startIcon={<ListIcon />} 
              onClick={() => safeNavigate('list')} 
              sx={{ color: T.teal, textTransform: 'none', fontWeight: 500, fontSize: 14 }}
            >
              Entitlement List
            </Button>
          </Box>
        </Paper>

        {/* Add Entitlements Section */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2, bgcolor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }} elevation={0}>
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: T.teal, mb: 2 }}>
            Add Entitlements
          </Typography>

          <Autocomplete
            options={availableEntitlements}
            getOptionLabel={(option) => option.name}
            value={selectedEntitlement}
            inputValue={searchInput}
            onInputChange={(event, newInputValue) => {
              setSearchInput(newInputValue);
            }}
            onChange={(event, newValue) => {
              if (newValue) {
                handleEntitlementSelect(newValue);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="medium"
                placeholder="Search or select entitlement to add..."
                variant="outlined"
                sx={searchBarSx}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <SearchIcon sx={{ fontSize: 20, color: T.teal, ml: 1, mr: 0.5 }} />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.name}>
                <Typography sx={{ fontWeight: 500, fontSize: 14 }}>{option.name}</Typography>
              </Box>
            )}
            disabled={availableEntitlements.length === 0}
            noOptionsText="No entitlements available to add"
            isOptionEqualToValue={(option, value) => option.name === value?.name}
            clearOnBlur={true}
            clearOnEscape={true}
            sx={{ mb: 2 }}
          />

          {/* ─── CHIPS SECTION ────────────────────────────── */}
          {selectedToAdd.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
              {selectedToAdd.map(ent => (
                <Chip
                  key={ent.name}
                  label={`${ent.name}: ${ent.value}`}
                  onDelete={() => handleRemoveChip(ent)}
                  size="medium"
                  sx={{ 
                    bgcolor: '#188C8E', 
                    color: 'white', 
                    fontWeight: 'bold',
                    fontSize: 13,
                    height: 34,
                    '& .MuiChip-label': { 
                      fontWeight: 'bold',
                      color: 'white',
                      px: 1.5
                    },
                    '& .MuiChip-deleteIcon': { 
                      color: 'white', 
                      fontSize: 18,
                      fontWeight: 'bold',
                      '&:hover': { 
                        color: '#FFCCCC',
                        transform: 'scale(1.1)'
                      }
                    },
                    '&:hover': { 
                      bgcolor: '#0F6E70',
                      transform: 'scale(1.02)'
                    }
                  }}
                />
              ))}
              <Typography sx={{ fontSize: 13, color: '#000000', ml: 1, alignSelf: 'center' }}>
                — not saved until Update
              </Typography>
            </Box>
          ) : (
            <Typography sx={{ fontSize: 14, color: '#000000', mt: 1 }}>No entitlements selected</Typography>
          )}
        </Paper>

        {/* Existing Entitlements Table */}
        <Paper sx={{ p: 3, borderRadius: 2, bgcolor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }} elevation={0}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 16, color: T.teal }}>
              Existing Entitlements
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                placeholder="Filter entitlements..."
                value={filterText}
                onChange={e => setFilterText(e.target.value)}
                sx={{ width: 220, ...searchBarSx }}
                InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 16, color: T.teal, mr: 1 }} /> }}
              />
              
              <Button
                size="medium"
                variant="outlined"
                startIcon={refreshing ? <CircularProgress size={18} /> : <RefreshIcon />}
                onClick={handleRefresh}
                disabled={refreshing}
                sx={{
                  fontSize: 13,
                  fontWeight: 500,
                  borderColor: T.border,
                  color: T.textPrimary,
                  textTransform: 'none',
                  minWidth: 100,
                  py: 0.5,
                  '&:hover': {
                    borderColor: T.teal,
                    backgroundColor: T.tealLight
                  }
                }}
              >
                {refreshing ? 'Refreshing...' : 'REFRESH'}
              </Button>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: 13, color: '#000000' }}>Page Size:</Typography>
                <Select 
                  size="small" 
                  value={pageSize} 
                  onChange={e => setPageSize(e.target.value)} 
                  sx={{ 
                    fontSize: 13, 
                    minWidth: 75,
                    borderRadius: 1,
                    color: '#000000',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: T.border,
                    }
                  }}
                >
                  {[10, 25, 50, 100].map(n => <MenuItem key={n} value={n} sx={{ fontSize: 13, color: '#000000' }}>{n}</MenuItem>)}
                </Select>
              </Box>
              
              <Typography sx={{ fontSize: 13, color: '#000000', whiteSpace: 'nowrap' }}>
                {startIndex}–{endIndex} of {filtered.length}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={() => setPage(prev => Math.max(1, prev - 1))}
                  disabled={page === 1 || filtered.length === 0}
                  sx={{ 
                    border: `1px solid ${T.border}`,
                    borderRadius: 1,
                    p: 0.5,
                    '&:hover': { backgroundColor: T.tealLight }
                  }}
                >
                  <PrevIcon sx={{ fontSize: 20 }} />
                </IconButton>
                
                <Typography sx={{ fontSize: 13, color: '#000000', mx: 1, whiteSpace: 'nowrap' }}>
                  Page {page} of {totalPages}
                </Typography>
                
                <IconButton
                  size="small"
                  onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={page === totalPages || filtered.length === 0}
                  sx={{ 
                    border: `1px solid ${T.border}`,
                    borderRadius: 1,
                    p: 0.5,
                    '&:hover': { backgroundColor: T.tealLight }
                  }}
                >
                  <NextIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>
            </Box>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: T.tealLight }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: 14, width: '35%', color: '#000000' }}>NAME</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 14, width: '20%', color: '#000000' }}>VALUE</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 14, width: '45%', color: '#000000' }}>DESCRIPTION</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <SkeletonRows count={6} />
                ) : paginated.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                      <Typography sx={{ fontSize: 14, color: '#000000' }}>No entitlements found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginated.map(ent => {
                    const isChanged = editedValues.hasOwnProperty(ent.name);
                    const rawVal = isChanged ? editedValues[ent.name] : ent.value;
                    const currentVal = rawVal === '' ? '' : rawVal;
                    
                    return (
                      <TableRow
                        key={ent.name}
                        sx={{
                          bgcolor: isChanged ? T.rowChanged : 'transparent',
                          borderLeft: isChanged ? `3px solid ${T.rowChangedBorder}` : '3px solid transparent',
                          '&:hover': { bgcolor: T.tealExtraLight }
                        }}
                      >
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 14, color: '#000000', fontWeight: 500 }}>{ent.name}</TableCell>
                        <TableCell>
                          {ent.type === 'feature' || ent.type === 'boolean' ? (
                            <Select
                              size="small"
                              value={currentVal === true ? 'true' : 'false'}
                              onChange={e => handleValueChange(ent.name, e.target.value === 'true')}
                              sx={{ 
                                fontSize: 14, 
                                width: 100,
                                minWidth: 100,
                                height: 40,
                                borderRadius: 1.5,
                                color: '#000000'
                              }}
                            >
                              <MenuItem value="true" sx={{ fontSize: 14, color: '#000000' }}>true</MenuItem>
                              <MenuItem value="false" sx={{ fontSize: 14, color: '#000000' }}>false</MenuItem>
                            </Select>
                          ) : (
                            <TextField
                              type="text"
                              size="small"
                              value={currentVal}
                              onChange={e => {
                                const rawValue = e.target.value;
                                if (rawValue === '') {
                                  handleValueChange(ent.name, '');
                                  return;
                                }
                                if (/^\d+$/.test(rawValue)) {
                                  const newValue = parseInt(rawValue, 10);
                                  handleValueChange(ent.name, newValue);
                                }
                              }}
                              onKeyDown={(e) => {
                                const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Home', 'End'];
                                if (allowedKeys.includes(e.key)) return;
                                if (!/^[0-9]$/.test(e.key) && e.key !== '') {
                                  e.preventDefault();
                                }
                              }}
                              sx={{ 
                                width: 100,
                                '& .MuiInputBase-root': { height: 40 }
                              }}
                              inputProps={{ 
                                style: { fontSize: 14, color: '#000000' }
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: 14, color: '#000000' }}>{ent.description}</Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Action Buttons - RESET and UPDATE */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mt: 4 }}>
          <Button
            variant="outlined"
            onClick={handleResetClick}
            disabled={updating || resetting || !hasUnsaved}
            startIcon={resetting ? <CircularProgress size={18} sx={{ color: '#DC2626' }} /> : <ResetIcon />}
            sx={{
              fontSize: 15,
              fontWeight: 600,
              px: 5,
              py: 1.2,
              background: 'linear-gradient(135deg, #FFF5F5 0%, #FFFFFF 100%)',
              border: '1.5px solid',
              borderColor: '#FCA5A5',
              color: '#DC2626',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background: 'linear-gradient(135deg, #FEE2E2 0%, #FFF5F5 100%)',
                borderColor: '#DC2626',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(220, 38, 38, 0.25)',
              },
              '&:active': {
                transform: 'translateY(0px)',
              },
              '&.Mui-disabled': {
                background: '#F5F5F5',
                borderColor: '#D1D5DB',
                color: '#9CA3AF',
              },
              borderRadius: 2,
              textTransform: 'none',
              letterSpacing: '0.5px',
            }}
          >
            {resetting ? 'Resetting...' : 'RESET'}
          </Button>

          <Button
            variant="contained"
            onClick={handleUpdateClick}
            disabled={updating || resetting}
            sx={{
              fontSize: 16,
              fontWeight: 700,
              px: 6,
              py: 1.2,
              background: 'linear-gradient(135deg, #188C8E 0%, #0F6E70 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1a9ea0 0%, #188C8E 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(24, 140, 142, 0.35)',
              },
              '&:active': {
                transform: 'translateY(0px)',
              },
              '&.Mui-disabled': {
                background: '#B0BEC5',
              },
              borderRadius: 2,
              minWidth: 200,
              textTransform: 'none',
              letterSpacing: '0.5px',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            {updating ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'UPDATE'}
          </Button>
        </Box>

        {/* Reset confirmation dialog */}
        <Dialog open={resetConfirmOpen} onClose={() => setResetConfirmOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontSize: 18, fontWeight: 700, color: '#000000' }}>
            Are you sure?
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: 14, color: '#000000', mb: 2 }}>
              You are about to reset all unsaved changes. This action cannot be undone.
            </Typography>
            {changeSummary.adding > 0 && (
              <Typography sx={{ fontSize: 13, color: '#188C8E', mb: 0.5, fontWeight: 500 }}>
                • {changeSummary.adding} pending entitlement(s) will be removed
              </Typography>
            )}
            {changeSummary.editing > 0 && (
              <Typography sx={{ fontSize: 13, color: '#F59E0B', mb: 0.5, fontWeight: 500 }}>
                • {changeSummary.editing} value change(s) will be reverted
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setResetConfirmOpen(false)} sx={{ color: '#000000', textTransform: 'none', fontSize: 14 }}>Cancel</Button>
            <Button onClick={() => { setResetConfirmOpen(false); handleReset(); }} variant="contained" sx={{ bgcolor: T.danger, '&:hover': { bgcolor: '#B91C1C' }, textTransform: 'none', fontSize: 14 }}>Reset</Button>
          </DialogActions>
        </Dialog>

        {/* Set Changes dialog */}
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontSize: 18, fontWeight: 700, color: '#000000' }}>
            Set Changes
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2, fontSize: 14, color: '#000000' }}>
              You are about to update <strong style={{ color: '#188C8E' }}>{displayCompanyName || companyNameState || companyId}</strong>:
            </Typography>
            {changeSummary.adding > 0 && (
              <Typography sx={{ fontSize: 13, color: '#188C8E', mb: 0.5, fontWeight: 500 }}>
                • Adding {changeSummary.adding} entitlement(s)
              </Typography>
            )}
            {changeSummary.editing > 0 && (
              <Typography sx={{ fontSize: 13, color: '#F59E0B', mb: 0.5, fontWeight: 500 }}>
                • Editing {changeSummary.editing} value(s)
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setConfirmOpen(false)} sx={{ color: '#000000', textTransform: 'none', fontSize: 14 }}>Cancel</Button>
            <Button onClick={handleUpdate} variant="contained" sx={{ bgcolor: '#188C8E', textTransform: 'none', fontSize: 14 }}>Update</Button>
          </DialogActions>
        </Dialog>

        {/* Unsaved changes warning */}
        <Dialog open={!!unsavedNavTarget} onClose={() => setUnsavedNavTarget(null)}>
          <DialogTitle sx={{ fontSize: 18, fontWeight: 700, color: '#000000', display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarnIcon sx={{ color: T.amber }} />
            Unsaved Changes
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: 14, color: '#000000' }}>
              You have unsaved changes. If you leave now, they will be discarded.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUnsavedNavTarget(null)} sx={{ color: '#000000', fontSize: 14 }}>Stay</Button>
            <Button onClick={confirmNavigation} variant="contained" color="error" sx={{ fontSize: 14 }}>Discard</Button>
          </DialogActions>
        </Dialog>

        {/* Entitlement list dialog */}
        <Dialog open={listOpen} onClose={() => setListOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ 
            bgcolor: '#188C8E', 
            color: 'white', 
            fontSize: 18, 
            fontWeight: 700, 
            py: 1.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>All Meltwater Entitlements</span>
            <Chip 
              label={`${entitlementListData.length} entitlements`} 
              size="small" 
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                fontWeight: 500
              }} 
            />
          </DialogTitle>
          <DialogContent sx={{ p: 2 }}>
            <TextField
              fullWidth
              size="medium"
              placeholder="Search entitlements by name..."
              value={listSearchText}
              onChange={(e) => setListSearchText(e.target.value)}
              sx={{ mb: 2, mt: 1, ...searchBarSx }}
              InputProps={{ 
                startAdornment: <SearchIcon sx={{ fontSize: 20, color: '#188C8E', mr: 1 }} />,
              }}
            />
            
            {listLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress sx={{ color: '#188C8E' }} />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table size="medium">
                    <TableHead>
                      <TableRow sx={{ bgcolor: T.tealLight }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: 14, color: '#000000', width: '25%' }}>
                          NAME
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 14, color: '#000000', width: '15%' }}>
                          TYPE
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 14, color: '#000000', width: '60%' }}>
                          DESCRIPTION
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredList.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                            <Typography sx={{ fontSize: 14, color: '#000000' }}>
                              {listSearchText ? 'No entitlements found matching your search' : 'No entitlements available'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredList.map(ent => (
                          <TableRow 
                            key={ent.name} 
                            hover 
                            sx={{ 
                              '&:hover': { bgcolor: T.tealExtraLight },
                              '&:nth-of-type(even)': { bgcolor: '#FAFAFA' }
                            }}
                          >
                            <TableCell sx={{ 
                              fontFamily: 'monospace', 
                              fontSize: 14, 
                              color: '#000000', 
                              fontWeight: 500 
                            }}>
                              {ent.name}
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={ent.type || 'feature'} 
                                size="small"
                                sx={{
                                  bgcolor: ent.type === 'quantity' ? '#E8F5E9' : '#E3F2FD',
                                  color: ent.type === 'quantity' ? '#2E7D32' : '#1565C0',
                                  fontWeight: 500,
                                  fontSize: 11
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontSize: 14, color: '#000000' }}>
                              {ent.description || ent.name}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {filteredList.length > 0 && (
                  <Typography sx={{ fontSize: 13, color: '#000000', mt: 2 }}>
                    Showing {filteredList.length} of {entitlementListData.length} entitlements
                  </Typography>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={() => { 
                setListOpen(false); 
                setListSearchText(''); 
              }} 
              sx={{ 
                fontSize: 14,
                color: '#188C8E',
                fontWeight: 600
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </Fade>
  );
};

export default EntitlementsTab;